import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ChevronRight, CheckCircle, AlertTriangle, AlertCircle } from "lucide-react";
import { jiraNotificationService } from "../services/JiraNotificationService";
import adaptUserService from "../users/services/AdaptUserService";

const RequestInstanceAccess = ({ isOpen, onClose, userInfo, accessibleInstances = [], canAccessDev = null }) => {
  const location = useLocation();
  const [selectedInstance, setSelectedInstance] = useState("");
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [errors, setErrors] = useState({});

  // Get instances from AdaptUserService and transform to array format
  const adaptInstances = Object.entries(adaptUserService.instances).map(([key, instance]) => ({
    id: key,
    name: instance.name,
    description: instance.description
  }));

  // Filter out instances that user already has access to
  const accessibleInstanceIds = accessibleInstances.map(instance => instance.id);
  let unavailableInstances = adaptInstances.filter(instance =>
    !accessibleInstanceIds.includes(instance.id)
  );

  // Hide developer-only instances (adapt-dev, adapt-tryout-test, etc.) from
  // the request dropdown for users who are not on the DEV allowlist in
  // public/config.json. Defaults to hiding while the allowlist check is
  // pending (canAccessDev === null) to avoid leaking the option.
  if (canAccessDev !== true) {
    unavailableInstances = unavailableInstances.filter(instance => {
      const isDevInstance = instance.id.includes('-dev-') ||
                           instance.id.includes('dev-') ||
                           instance.name.toLowerCase().includes('dev') ||
                           instance.id === 'adapt-tryout-test';
      return !isDevInstance;
    });
  }

  // Append the "don't know" option to unavailable instances
  const instances = [
    ...unavailableInstances,
    {
      id: "dont-know",
      name: "Not sure which instance to choose?",
      description: ""
    }
  ];

  const getInstanceName = (instanceId) => {
    const instance = instances.find(inst => inst.id === instanceId);
    return instance ? instance.name : instanceId;
  };

  const navigateToNotification = (type, requestDetails, notificationResult) => {
    // Show inline notification instead of navigating
    setNotificationData({
      type,
      requestType: 'instance-access',
      requestDetails,
      notificationResult
    });
    setShowNotification(true);
  };

  const handleCancel = () => {
    setSelectedInstance("");
    setReason("");
    setErrors({});
    setShowNotification(false);
    setNotificationData(null);
    onClose();
  };

  const handleOkClick = () => {
    // Navigate back to the parent page or close modal
    setShowNotification(false);
    setNotificationData(null);
    onClose();
  };

  const handleSubmit = async () => {
    // Validate form
    const newErrors = {};
    if (!selectedInstance) {
      newErrors.instance = 'Please select an instance';
    }
    if (!reason.trim()) {
      newErrors.reason = 'Please provide a reason for your request';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    console.log('🚀 Instance Access Request submission started');
    setIsLoading(true);
    
    try {
      // Get user email from props, location state, or fallback
      const userEmail = userInfo?.email || 
                       location.state?.email || 
                       location.state?.user || 
                       userInfo?.user || 
                       "user@example.com";
      
      const instanceName = getInstanceName(selectedInstance);
      
      // Extract name parts from email for better user information
      const emailParts = userEmail.split('@')[0].split('.');
      const firstName = emailParts[0] ? emailParts[0].charAt(0).toUpperCase() + emailParts[0].slice(1) : 'User';
      const lastName = emailParts[1] ? emailParts[1].charAt(0).toUpperCase() + emailParts[1].slice(1) : 'Name';
      
      const requestDetails = {
        userEmail: userEmail,
        firstName: firstName,
        lastName: lastName,
        selectedInstance: selectedInstance,
        instanceName: instanceName,
        reason: reason.trim(),
        companyName: userInfo?.companyName || 'Not specified',
        submittedAt: new Date().toLocaleString()
      };

      console.log('📧 [Instance Access Request] Request details:', requestDetails);

      const notificationResult = await jiraNotificationService.sendInstanceAccessRequestNotification(requestDetails);
      
      // Show inline notification instead of navigating
      if (notificationResult.success) {
        navigateToNotification('success', requestDetails, notificationResult);
      } else {
        navigateToNotification('warning', requestDetails, notificationResult);
      }
      
    } catch (error) {
      console.error('❌ [Instance Access Request] Submission error:', error);
      navigateToNotification('error', null, { error: error.message });
    } finally {
      setIsLoading(false);
      // Reset form
      setSelectedInstance("");
      setReason("");
    }
  };

  if (!isOpen) return null;

  // Notification content renderer
  const renderNotificationContent = () => {
    if (!notificationData) return null;

    const { type, requestDetails, notificationResult } = notificationData;

    const getIcon = () => {
      switch (type) {
        case 'success':
          return <CheckCircle className="w-12 h-12 text-green-500 mb-4" />;
        case 'warning':
          return <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />;
        case 'error':
          return <AlertCircle className="w-12 h-12 text-red-500 mb-4" />;
        default:
          return <CheckCircle className="w-12 h-12 text-green-500 mb-4" />;
      }
    };

    const getTitle = () => {
      switch (type) {
        case 'success':
          return 'Request Sent Successfully!';
        case 'warning':
          return 'Request Sent with Issues';
        case 'error':
          return 'Request Failed';
        default:
          return 'Request Processed';
      }
    };

    const getMessage = () => {
      switch (type) {
        case 'success':
          return `Your request for instance access has been sent to the admin. We’ll share the details with you once it’s approved.`;
        case 'warning':
          return `Your request was sent but there might be some issues. ${notificationResult?.message || 'Please contact support if you don\'t receive a confirmation email.'}`;
        case 'error':
          return `Failed to send your request. ${notificationResult?.error || 'Please try again or contact support.'}`;
        default:
          return 'Your request has been processed.';
      }
    };

    return (
      <div className="p-6 text-center">
        <div className="flex flex-col items-center">
          {getIcon()}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {getTitle()}
          </h3>
          <p className="text-gray-600 text-sm mb-6 max-w-md">
            {getMessage()}
          </p>
          
          {requestDetails && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left w-full max-w-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Request Details:</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <div><span className="font-medium">Instance:</span> {requestDetails.instanceName}</div>
                <div><span className="font-medium">Email:</span> {requestDetails.userEmail}</div>
                <div><span className="font-medium">Submitted:</span> {requestDetails.submittedAt}</div>
              </div>
            </div>
          )}

          <button
            onClick={handleOkClick}
            className="px-6 py-2 text-sm font-medium text-white bg-brand-blue border border-transparent rounded-md hover:bg-brand-darkBlue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 text-left">
            {showNotification ? 'Request Status' : 'Request Instance Access'}
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Show either form or notification */}
        {showNotification ? (
          <div className="flex-1 overflow-y-auto">
            {renderNotificationContent()}
          </div>
        ) : (
          <>
            {/* Form Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 text-left">
          <p className="text-gray-600 text-sm text-left">
            Select the instance you need access to and provide a reason for your request.
          </p>

          {/* Instance Selection */}
          <div className="text-left">
            <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
              Select Instance <span className="text-error-500">*</span>
            </label>
            <div className="space-y-3">
              {instances.map((instance) => (
                <label
                  key={instance.id}
                  className={`flex items-start space-x-3 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 transition-colors text-left ${
                    errors.instance ? 'border-error-500' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="instance"
                    value={instance.id}
                    checked={selectedInstance === instance.id}
                    onChange={(e) => {
                      setSelectedInstance(e.target.value);
                      setErrors(prev => ({ ...prev, instance: '' }));
                    }}
                    className="mt-1 w-4 h-4 text-brand-blue border-gray-300 focus:ring-brand-blue focus:ring-2"
                  />
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium text-gray-900 text-left">
                      {instance.name}
                    </div>
                    {instance.description && (
                      <div className="text-sm text-gray-500 mt-1 text-left">
                        {instance.description}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
            {errors.instance && (
              <p className="text-error-500 text-sm mt-1">{errors.instance}</p>
            )}
          </div>

          {/* Reason for Request */}
          <div className="text-left">
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Reason for request <span className="text-error-500">*</span>
            </label>
            <textarea
              id="reason"
              rows={4}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setErrors(prev => ({ ...prev, reason: '' }));
              }}
              placeholder="Please explain why you need access to these instance. Include details about your project, role, or specific use case..."
              className={`w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-sm resize-none text-left ${
                errors.reason ? 'border-error-500' : 'border-gray-300'
              }`}
            />
            {errors.reason && (
              <p className="text-error-500 text-sm mt-1">{errors.reason}</p>
            )}
            <p className="text-xs text-gray-500 mt-2 text-left">
              <span className="font-bold">Note:</span> Providing a detailed reason helps admin approve your request faster.
            </p>
          </div>
            </div>

            {/* Footer - Fixed */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-blue border border-transparent rounded-md hover:bg-brand-darkBlue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
                {isLoading ? 'Sending Request...' : 'Send Request'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestInstanceAccess;